import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * One-time Nation ownership claim.
 *
 * While the DB has no `is_owner=true` user, this route accepts a real-world
 * email + chosen password + name and creates the account with unrestricted
 * access to every module. Immediately after claim the user is logged in and
 * routed to /portal.
 *
 * Once claimed, the /setup route redirects to /login.
 */
export default function Setup() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null); // {owner_claimed, setup_code_required}
  const [form, setForm] = useState({
    first_name: "", last_name: "", title: "",
    email: "", password: "", password_confirm: "", setup_code: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/setup-status")
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ owner_claimed: false, setup_code_required: false }));
  }, []);

  useEffect(() => {
    if (status && status.owner_claimed) {
      // Already claimed — surface a hint and route.
      toast.info("Nation ownership already claimed. Please sign in.");
      nav("/login", { replace: true });
    }
  }, [status, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.password_confirm) {
      setErr("Passwords do not match.");
      return;
    }
    if (form.password.length < 10) {
      setErr("Password must be at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/claim-ownership", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        title: form.title.trim() || null,
        setup_code: form.setup_code || null,
      });
      await refresh();
      toast.success("Ownership claimed. Welcome.");
      nav("/portal", { replace: true });
    } catch (e) {
      setErr(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center font-mono text-xs text-white/40 tracking-widest uppercase">
        Reading setup state…
      </div>
    );
  }
  if (status.owner_claimed) return null;

  return (
    <div className="min-h-[80vh] px-6 py-16 flex justify-center items-start">
      <div className="max-w-xl w-full">
        <div className="label-mono">Nation setup</div>
        <h1 className="font-display text-4xl mt-3 leading-tight">
          Claim ownership of the <span className="text-white/70">Kasabonika Lake</span> portal.
        </h1>
        <p className="mt-5 text-white/60 leading-relaxed text-sm">
          This screen appears only once. Create the Nation's unrestricted owner account with
          the credentials you will actually use. Every other role, permission and setting is
          controllable from this account.
        </p>

        <div className="mt-8 border border-white/10 bg-white/[0.02] p-6" data-testid="setup-form">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="label-mono mb-1">First name</div>
                <input required value={form.first_name} onChange={e=>setForm({...form, first_name: e.target.value})}
                  className="w-full px-3 py-2 text-sm" data-testid="setup-first-name"/>
              </label>
              <label className="block">
                <div className="label-mono mb-1">Last name</div>
                <input required value={form.last_name} onChange={e=>setForm({...form, last_name: e.target.value})}
                  className="w-full px-3 py-2 text-sm" data-testid="setup-last-name"/>
              </label>
            </div>
            <label className="block">
              <div className="label-mono mb-1">Title (optional)</div>
              <input value={form.title} onChange={e=>setForm({...form, title: e.target.value})}
                placeholder="e.g. Chief Administrative Officer"
                className="w-full px-3 py-2 text-sm" data-testid="setup-title"/>
            </label>
            <label className="block">
              <div className="label-mono mb-1">Email</div>
              <input type="email" required value={form.email} onChange={e=>setForm({...form, email: e.target.value})}
                autoComplete="username"
                className="w-full px-3 py-2 text-sm" data-testid="setup-email"/>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="label-mono mb-1">Password</div>
                <input type="password" required minLength={10} value={form.password} onChange={e=>setForm({...form, password: e.target.value})}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 text-sm" data-testid="setup-password"/>
              </label>
              <label className="block">
                <div className="label-mono mb-1">Confirm</div>
                <input type="password" required minLength={10} value={form.password_confirm} onChange={e=>setForm({...form, password_confirm: e.target.value})}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 text-sm" data-testid="setup-password-confirm"/>
              </label>
            </div>
            {status.setup_code_required && (
              <label className="block">
                <div className="label-mono mb-1">Setup code</div>
                <input required value={form.setup_code} onChange={e=>setForm({...form, setup_code: e.target.value})}
                  placeholder="Provided by your deployment operator"
                  className="w-full px-3 py-2 text-sm font-mono" data-testid="setup-code"/>
              </label>
            )}
            {err && <div className="text-[color:var(--signal)] text-sm">{err}</div>}
            <button type="submit" disabled={busy} className="btn-cta primary w-full justify-center" data-testid="setup-submit">
              <ShieldCheck size={14}/>
              {busy ? "Claiming…" : "Claim ownership"}
            </button>
          </form>
        </div>

        <div className="mt-6 font-mono text-[10px] text-white/40 uppercase tracking-widest">
          After claiming, this route is permanently sealed.
        </div>
      </div>
    </div>
  );
}
